import { useContext } from "react";
import { ChatContext, curCourse, curSessionIdAtom, userAtom } from "../store";
import { CurrentUserType, getToken } from "../polyfills";
import { v4 as uuidv4 } from "uuid";
import {
    ChatMessageItem,
    FileMessageItem,
    MsgType,
    OperatorTypeType
} from "../types";
import { useAtomValue } from "jotai";
import moment from "moment";
import { message } from "antd";
import { makeFileMessageContent } from "./common";

export function useSender() {
    const { sendMessage } = useContext(ChatContext);
    const curSessionId = useAtomValue(curSessionIdAtom);
    const courseInfo = useAtomValue(curCourse); //课程信息

    const { userNo, userName } = useAtomValue(userAtom);

    // 发送文字信息
    function sendTextMessage(content: string): ChatMessageItem | void {
        if (!curSessionId) throw new Error("无法找到当前会话");

        const messageData = {
            guid: uuidv4(),
            content,
            type: MsgType.TEXT,
            operatorType: OperatorTypeType.Normal,
            sessionUid: curSessionId, //会话guid
            ...getCourseInfo(),
            sender: getCurrentSender()
        };

        const result = sendMessageWithException(messageData);

        if (!result) {
            message.error("消息发送失败");
            return;
        }

        return {
            ...messageData,
            ...getExtraInfo()
        };
    }

    // 上传文件
    function sendFileMessage(file: File) {
        if (!curSessionId) throw new Error("无法找到当前会话");

        const messageData = {
            guid: uuidv4(),
            content: makeFileMessageContent(file),
            type: MsgType.FILE,
            operatorType: OperatorTypeType.Normal,
            sessionUid: curSessionId, //会话guid
            ...getCourseInfo(),
            sender: getCurrentSender()
        };

        // 上传完成以后回调
        const sendToServer = (url: string) => {
            messageData.content = makeFileMessageContent(file, url);
            const result = sendMessageWithException(messageData);

            if (!result) {
                message.error("文件发送失败");
                return result;
            }
            return result;
        };

        const messageInfo: FileMessageItem = {
            uploading: true,
            ...messageData,
            ...getExtraInfo()
        };

        return {
            sendToServer,
            messageInfo
        };
    }

    // 撤回消息
    function retractMessage(message: ChatMessageItem) {
        message.operatorType = OperatorTypeType.Retract;
        sendMessageWithException(message);
    }

    // 举报消息
    function reportMessage(message: ChatMessageItem) {
        const content = JSON.stringify({
            sessionGuid: message?.sessionUid,
            messageGuid: message?.guid
        });

        const messageData = {
            guid: uuidv4(),
            content,
            type: MsgType.TEXT,
            operatorType: OperatorTypeType.Report,
            sessionUid: curSessionId, //会话guid
            ...getCourseInfo(),
            sender: getCurrentSender()
        };

        sendMessageWithException(messageData);
    }

    function sendMessageWithException(data: any): boolean {
        try {
            sendMessage(wrapMessage(data));
            return true;
        } catch (e) {
            return false;
        }
    }

    function wrapMessage<T>(messageJson: T) {
        const token = getToken();
        return JSON.stringify({
            businessKey: "chat",
            token,
            messageJson
        });
    }

    function getCourseInfo() {
        if (!courseInfo) return {};
        return courseInfo;
    }

    function getExtraInfo() {
        return {
            sendDate: moment().format("YYYY-MM-DD HH:mm:ss"),
            delStatus: "0"
        };
    }

    // 当前登陆人
    function getCurrentSender() {
        return {
            no: userNo, //发送人编号
            name: userName, //发送人姓名
            type: CurrentUserType //发送人类型0教师1学生
        };
    }

    return {
        sendTextMessage,
        sendFileMessage,
        retractMessage,
        reportMessage
    };
}
